import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { AdultAuthService } from '../../platform/auth/adult-auth';
import { IDLE_LIMIT_MS, IDLE_WARNING_MS } from './idleTimeout';

// Signs an adult out after IDLE_LIMIT_MS without any activity -- including
// time the tab was closed or in the background: the last-activity time is
// kept in localStorage (shared by every tab of the site), so reopening the
// site after a long break signs out straight away, and activity in any one
// tab keeps them all signed in. A warning shows IDLE_WARNING_MS before the
// limit, with a "Stay signed in" button.
//
// Only adult areas are watched. Learners have no account (they use an entry
// code), and the public pages have nothing to protect. Activity on the
// sign-in pages still counts, so someone who has just logged in is never
// signed straight back out because of an old timestamp.
//
// The same check also notices when the session has gone (signed out in
// another tab, or expired) and leaves the protected page instead of showing
// it until the next request fails.

const LAST_ACTIVITY_KEY = 'dodoskills.lastActivityAt';
const CHECK_EVERY_MS = 15_000;
const RECORD_AT_MOST_EVERY_MS = 5_000;

function readLastActivity(): number | null {
  try {
    const raw = localStorage.getItem(LAST_ACTIVITY_KEY);
    const value = raw === null ? NaN : Number(raw);
    return Number.isFinite(value) ? value : null;
  } catch {
    return null;
  }
}

function writeLastActivity(at: number) {
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(at));
  } catch {
    // Storage blocked: the timer then only covers this open tab.
  }
}

function isWatchedPath(path: string) {
  return path.startsWith('/adult') || path === '/connect';
}

type Props = {
  auth: Pick<AdultAuthService, 'restoreSession' | 'signOutThisDevice'>;
};

export function IdleSignOut({ auth }: Props) {
  const location = useLocation();
  const navigate = useNavigate();
  const [warning, setWarning] = useState(false);
  const lastRecorded = useRef(0);
  const signingOut = useRef(false);
  const watched = isWatchedPath(location.pathname);
  const onAuthPage = location.pathname.startsWith('/auth');

  const recordActivity = useCallback((force = false) => {
    const now = Date.now();
    if (!force && now - lastRecorded.current < RECORD_AT_MOST_EVERY_MS) return;
    lastRecorded.current = now;
    writeLastActivity(now);
    setWarning(false);
  }, []);

  // Arriving on a sign-in page always counts as fresh activity.
  useEffect(() => {
    if (onAuthPage) recordActivity(true);
  }, [onAuthPage, location.pathname, recordActivity]);

  // Any real interaction, anywhere on the site, counts as activity.
  useEffect(() => {
    const onActivity = () => {
      recordActivity();
    };
    const events = [
      'pointerdown',
      'keydown',
      'scroll',
      'touchstart',
      'mousemove',
    ] as const;
    for (const event of events)
      window.addEventListener(event, onActivity, { passive: true });
    return () => {
      for (const event of events) window.removeEventListener(event, onActivity);
    };
  }, [recordActivity]);

  const check = useCallback(async () => {
    if (!watched || signingOut.current) {
      setWarning(false);
      return;
    }
    const session = await auth.restoreSession().catch(() => null);
    if (!session) {
      // Signed out elsewhere, or expired: leave the protected page.
      setWarning(false);
      void navigate('/auth/sign-in', { replace: true });
      return;
    }
    const last = readLastActivity();
    if (last === null) {
      recordActivity(true);
      return;
    }
    const idleFor = Date.now() - last;
    if (idleFor >= IDLE_LIMIT_MS) {
      signingOut.current = true;
      setWarning(false);
      try {
        await auth.signOutThisDevice();
      } finally {
        signingOut.current = false;
        void navigate('/auth/sign-in?reason=inactive', { replace: true });
      }
      return;
    }
    setWarning(idleFor >= IDLE_LIMIT_MS - IDLE_WARNING_MS);
  }, [auth, navigate, recordActivity, watched]);

  // Check on every page change, on a timer, and whenever the tab comes back
  // into view (a laptop waking up, or switching back to the tab).
  useEffect(() => {
    void check();
    const timer = window.setInterval(() => void check(), CHECK_EVERY_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [check]);

  if (!warning || !watched) return null;
  return (
    <div
      role="alertdialog"
      aria-labelledby="idle-warning-title"
      aria-describedby="idle-warning-text"
      className="fixed inset-x-4 bottom-4 z-50 mx-auto grid max-w-md gap-3 rounded-card border border-accent/30 bg-white p-5 shadow-card sm:bottom-6"
    >
      <p id="idle-warning-title" className="font-bold text-ink">
        You&apos;ll be signed out soon
      </p>
      <p id="idle-warning-text" className="text-sm text-ink-soft">
        You haven&apos;t been active for a while. To keep information safe,
        you&apos;ll be signed out in about 2 minutes.
      </p>
      <button
        type="button"
        onClick={() => {
          recordActivity(true);
        }}
        className="min-h-11 w-fit rounded-lg bg-forest-950 px-5 font-semibold text-white hover:bg-forest-800"
      >
        Stay signed in
      </button>
    </div>
  );
}
